import * as React from "react";
import { createContext, ReactNode, useCallback } from "react";
import { Configuration, JobControllerApi, JobInfo, VinaJobInfo } from "./api/openapi-generator";
import fetchBuilder from "fetch-retry";
import { useReportLoader } from "@loschmidt/commons/report-loader";
import { resilientRetryStrategy } from "@loschmidt/commons";
import { basePath } from "./settings";

type JobInfoContextType = ReturnType<typeof useProviderValue>;

export const JobInfoContext = createContext<JobInfoContextType>(null as any);

export function JobInfoContextProvider({id, children}: { id: string, children: ReactNode }) {
  const value = useProviderValue(id);
  return (
    <JobInfoContext.Provider value={value}>{children}</JobInfoContext.Provider>
  )
}

function useProviderValue(id: string) {

  const isFinal = useCallback((report: JobInfo | VinaJobInfo) => report.status === 5, []);

  const fetchJobInfo = useCallback(async (initOverrides: RequestInit) => {
    return await JobInfoApi.jobInfo({id}, initOverrides);
  }, [id]);

  const fetchVinaJobInfo = useCallback(async (initOverrides: RequestInit) => {
    return await JobInfoApi.vinaJobInfo({id}, initOverrides);
  }, [id]);

  const {
    report: jobInfo,
    fetchState: fetchState,
  } = useReportLoader<JobInfo>({
    isFinal,
    fetchReport: fetchJobInfo,
  });

  const {
    report: vinaJobInfo,
    fetchState: vinaFetchState,
  } = useReportLoader<VinaJobInfo>({
    isFinal,
    fetchReport: fetchVinaJobInfo,
  });

  return {
    id,
    jobInfo: (jobInfo ?? null) as (JobInfo | null),
    fetchState,
    vinaJobInfo: (vinaJobInfo ?? null) as (VinaJobInfo | null),
    vinaFetchState,
  }
}

// We define a custom configuration with a retry logic specific for fetching the job info.
// The idea is to keep retrying even when the backend is down.
const JobInfoApi = new JobControllerApi(new Configuration({
  basePath: basePath,
  fetchApi: fetchBuilder(fetch, {
    retryDelay: 5000,
    retryOn: resilientRetryStrategy,
  })
}))
